import type { Skeleton } from 'three'
import type { Mesh2MotionEngine } from '../Mesh2MotionEngine.ts'
import { ModalDialog } from './ModalDialog.ts'
import { confirm_action } from './ConfirmationDialog.ts'
import { escape_html } from './html.ts'
import { WeightPaintTool, type WeightPaintMode } from '../features/timeline/WeightPaintTool.ts'
import { VertexInfluenceInspector } from '../features/rig-quality/VertexInfluenceInspector.ts'
import { LiveRigQualityPanel } from '../features/rig-quality/LiveRigQualityPanel.ts'
import { FootFloorAlign } from '../features/timeline/FootFloorAlign.ts'
import {
  PoseLibraryStorage,
  capture_pose_for_library,
  apply_pose_to_skeleton,
  generate_pose_id,
  type SavedPose
} from '../features/persistence/PoseLibraryStorage.ts'

/**
 * Wires up the engine's toolbar-button panels: weight paint, animation layers,
 * morph targets, lift-to-floor, live rig quality, pose library, vertex inspector.
 *
 * These all share the same shape — a button in the animations toolbar that
 * opens a modal or toggles a tool — and they're not used by the retarget app,
 * so they live outside the engine class to keep `Mesh2MotionEngine` focused on
 * core orchestration.
 */
export function mount_engine_panels (engine: Mesh2MotionEngine): void {
  mount_weight_paint(engine)
  mount_animation_layers(engine)
  mount_morph_targets(engine)
  mount_lift_to_floor(engine)
  mount_live_rig_quality(engine)
  mount_pose_library(engine)
  mount_vertex_inspector(engine)
}

function on_dom_ready (fn: () => void): void {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn)
  else fn()
}

function set_toggle_state (btn: HTMLButtonElement, active: boolean): void {
  btn.setAttribute('aria-pressed', String(active))
  btn.style.background = active ? 'rgba(255,158,0,0.25)' : ''
}

// ────────────────────────────────────────────────────────────────────────────
// Weight paint

function mount_weight_paint (engine: Mesh2MotionEngine): void {
  engine.weight_paint_tool = new WeightPaintTool({
    canvas: engine.renderer.domElement,
    camera: engine.camera,
    get_active_bone_index: () => {
      const meshes = engine.weight_skin_step.final_skinned_meshes()
      if (meshes.length === 0) return -1
      const selected = engine.edit_skeleton_step.get_currently_selected_bone()
      if (selected === null) return -1
      return meshes[0].skeleton.bones.indexOf(selected)
    },
    get_meshes: () => engine.weight_skin_step.final_skinned_meshes()
  })
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#weight-paint-button')
    if (btn === null || engine.weight_paint_tool === undefined) return
    btn.addEventListener('click', () => { open_weight_paint_dialog(engine, btn) })
  })
}

function open_weight_paint_dialog (engine: Mesh2MotionEngine, toggle_btn: HTMLButtonElement): void {
  if (engine.weight_paint_tool === undefined) return
  const tool = engine.weight_paint_tool
  const html = `
    <div style="display:flex; flex-direction:column; gap:0.6rem; min-width:300px;">
      <p style="margin:0;">Click a vertex on the mesh to apply.</p>
      <label style="display:flex; gap:0.5rem; align-items:center;">
        <input type="radio" name="weight-paint-mode" value="paint" ${tool.get_mode() === 'paint' ? 'checked' : ''} />
        Paint — assign clicked vertex fully to the selected bone
      </label>
      <label style="display:flex; gap:0.5rem; align-items:center;">
        <input type="radio" name="weight-paint-mode" value="smooth" ${tool.get_mode() === 'smooth' ? 'checked' : ''} />
        Smooth — average weights with 6 nearest neighbours
      </label>
      <p style="margin:0; opacity:0.75; font-size:0.85em;">Tip: select the bone you want to paint to in the skeleton list (or via Pose Mode click). The currently-selected bone is what Paint targets.</p>
      <div style="display:flex; gap:0.5rem;">
        <button id="weight-paint-toggle-btn" class="secondary-button">${tool.is_active() ? 'Stop painting' : 'Start painting'}</button>
      </div>
    </div>
  `
  const dialog = new ModalDialog('Weight paint', html)
  dialog.show()
  document.querySelectorAll<HTMLInputElement>('input[name="weight-paint-mode"]').forEach((r) => {
    r.addEventListener('change', () => { tool.set_mode(r.value as WeightPaintMode) })
  })
  document.querySelector<HTMLButtonElement>('#weight-paint-toggle-btn')?.addEventListener('click', () => {
    const next = !tool.is_active()
    tool.set_active(next)
    set_toggle_state(toggle_btn, next)
    dialog.remove()
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Animation layers

function mount_animation_layers (engine: Mesh2MotionEngine): void {
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#animation-layers-button')
    if (btn === null) return
    btn.addEventListener('click', () => { open_animation_layers_dialog(engine) })
  })
}

function open_animation_layers_dialog (engine: Mesh2MotionEngine): void {
  const meshes = engine.weight_skin_step.final_skinned_meshes()
  if (meshes.length === 0) {
    new ModalDialog('Animation layers', 'No bound mesh — finish the rig first.').show()
    return
  }
  const skinned_mesh = meshes[0]
  const clips = engine.animations_listing_step.get_loaded_clip_pairs?.() ?? []
  if (clips.length === 0) {
    new ModalDialog('Animation layers', 'Load some animations first.').show()
    return
  }
  const active_names = new Set(engine.animation_layer_manager.list_active_layers().map(l => l.display_name))
  const rows = clips.map((pair, i) => {
    const name = pair.display_animation_clip.name
    const is_active = active_names.has(name)
    const layer = engine.animation_layer_manager.list_active_layers().find(l => l.display_name === name)
    const weight = layer?.weight ?? 1.0
    return `
      <li data-clip-index="${i}" style="display:flex; align-items:center; gap:0.5rem; padding:0.25rem 0;">
        <label style="display:flex; align-items:center; gap:0.4rem; flex:1; min-width:0;">
          <input type="checkbox" data-layer-toggle ${is_active ? 'checked' : ''} />
          <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escape_html(name)}</span>
        </label>
        <input type="range" data-layer-weight min="0" max="1" step="0.05" value="${weight}" ${is_active ? '' : 'disabled'} style="width:120px;" />
        <span data-layer-weight-readout style="min-width:32px; text-align:right; font-variant-numeric: tabular-nums;">${weight.toFixed(2)}</span>
      </li>`
  }).join('')

  const html = `
    <div style="min-width:380px;">
      <p style="margin-top:0;">Pick clips to play as additive layers on top of the base animation.</p>
      <ul style="list-style:none; padding:0; margin:0; max-height:280px; overflow-y:auto;">${rows}</ul>
    </div>`
  const dialog = new ModalDialog('Animation layers', html)
  dialog.show()

  document.querySelectorAll<HTMLLIElement>('li[data-clip-index]').forEach((li) => {
    const idx = parseInt(li.getAttribute('data-clip-index') ?? '-1', 10)
    const pair = clips[idx]
    if (pair === undefined) return
    const toggle = li.querySelector<HTMLInputElement>('[data-layer-toggle]')
    const weight_input = li.querySelector<HTMLInputElement>('[data-layer-weight]')
    const weight_readout = li.querySelector<HTMLSpanElement>('[data-layer-weight-readout]')
    toggle?.addEventListener('change', () => {
      if (toggle.checked) {
        const w = parseFloat(weight_input?.value ?? '1')
        engine.animation_layer_manager.add_layer(engine.animations_listing_step.mixer(), skinned_mesh, pair.display_animation_clip, w)
        if (weight_input !== null) weight_input.disabled = false
      } else {
        engine.animation_layer_manager.remove_layer(pair.display_animation_clip.name)
        if (weight_input !== null) weight_input.disabled = true
      }
    })
    weight_input?.addEventListener('input', () => {
      const w = parseFloat(weight_input.value)
      if (Number.isFinite(w)) {
        engine.animation_layer_manager.set_layer_weight(pair.display_animation_clip.name, w)
        if (weight_readout !== null) weight_readout.textContent = w.toFixed(2)
      }
    })
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Morph targets

function mount_morph_targets (engine: Mesh2MotionEngine): void {
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#morph-targets-button')
    if (btn === null) return
    btn.addEventListener('click', () => { open_morph_targets_dialog(engine) })
  })
}

function open_morph_targets_dialog (engine: Mesh2MotionEngine): void {
  const meshes = engine.weight_skin_step.final_skinned_meshes()
  if (meshes.length === 0) {
    new ModalDialog('Morph targets', 'No bound mesh — finish the rig first.').show()
    return
  }
  interface MorphRef { mesh_index: number, name: string, target_index: number }
  const refs: MorphRef[] = []
  meshes.forEach((mesh, mi) => {
    const dict = mesh.morphTargetDictionary
    const influences = mesh.morphTargetInfluences
    if (dict === undefined || influences === undefined) return
    Object.entries(dict).forEach(([name, target_index]) => {
      refs.push({ mesh_index: mi, name, target_index })
    })
  })
  if (refs.length === 0) {
    new ModalDialog('Morph targets', 'This mesh has no morph targets / blend shapes baked in.').show()
    return
  }
  const rows = refs.map((ref, ri) => {
    const current = meshes[ref.mesh_index].morphTargetInfluences?.[ref.target_index] ?? 0
    return `
      <li data-ref-index="${ri}" style="display:flex; align-items:center; gap:0.5rem; padding:0.2rem 0;">
        <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${escape_html(ref.name)}</span>
        <input type="range" data-morph-input min="0" max="1" step="0.01" value="${current}" style="width:140px;" />
        <span data-morph-readout style="min-width:36px; text-align:right; font-variant-numeric: tabular-nums;">${current.toFixed(2)}</span>
      </li>`
  }).join('')
  const html = `
    <div style="min-width:360px;">
      <p style="margin-top:0;">Drag to set the influence for each morph target (0 = off, 1 = full).</p>
      <ul style="list-style:none; padding:0; margin:0; max-height:320px; overflow-y:auto;">${rows}</ul>
    </div>`
  const dialog = new ModalDialog('Morph targets', html)
  dialog.show()

  document.querySelectorAll<HTMLLIElement>('li[data-ref-index]').forEach((li) => {
    const ri = parseInt(li.getAttribute('data-ref-index') ?? '-1', 10)
    const ref = refs[ri]
    if (ref === undefined) return
    const input = li.querySelector<HTMLInputElement>('[data-morph-input]')
    const readout = li.querySelector<HTMLSpanElement>('[data-morph-readout]')
    input?.addEventListener('input', () => {
      const v = parseFloat(input.value)
      if (!Number.isFinite(v)) return
      const influences = meshes[ref.mesh_index].morphTargetInfluences
      if (influences !== undefined) influences[ref.target_index] = v
      if (readout !== null) readout.textContent = v.toFixed(2)
    })
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Lift to floor

function mount_lift_to_floor (engine: Mesh2MotionEngine): void {
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#lift-to-floor-button')
    if (btn === null) return
    btn.addEventListener('click', () => {
      const skeleton: Skeleton | undefined = engine.weight_skin_step.skeleton()
      if (skeleton === undefined) {
        new ModalDialog('Lift to floor', 'No bound skeleton — finish the rig first.').show()
        return
      }
      const result = FootFloorAlign.lift_pose_to_floor(skeleton, 0)
      if (!result.applied) {
        new ModalDialog('Lift to floor', result.lowest_foot_bone === null
          ? 'No foot/toe bones detected by name. Add bones named like "foot", "toe", "ankle", or "heel".'
          : `Already on the floor (lowest foot bone: ${result.lowest_foot_bone}).`).show()
      }
    })
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Live rig quality

function mount_live_rig_quality (engine: Mesh2MotionEngine): void {
  engine.live_rig_quality_panel = new LiveRigQualityPanel({ scene: engine.scene })
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#rig-quality-toggle-button')
    if (btn === null || engine.live_rig_quality_panel === undefined) return
    btn.addEventListener('click', () => {
      if (engine.live_rig_quality_panel === undefined) return
      const next = !engine.live_rig_quality_panel.is_active()
      engine.live_rig_quality_panel.set_active(next)
      set_toggle_state(btn, next)
    })
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Vertex inspector

function mount_vertex_inspector (engine: Mesh2MotionEngine): void {
  engine.vertex_inspector = new VertexInfluenceInspector({
    canvas: engine.renderer.domElement,
    camera: engine.camera,
    get_meshes: () => engine.weight_skin_step.final_skinned_meshes()
  })
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#vertex-inspector-button')
    if (btn === null || engine.vertex_inspector === undefined) return
    btn.addEventListener('click', () => {
      if (engine.vertex_inspector === undefined) return
      const next = !engine.vertex_inspector.is_active()
      engine.vertex_inspector.set_active(next)
      set_toggle_state(btn, next)
    })
  })
}

// ────────────────────────────────────────────────────────────────────────────
// Pose library

function mount_pose_library (engine: Mesh2MotionEngine): void {
  on_dom_ready(() => {
    const btn = document.querySelector<HTMLButtonElement>('#pose-library-button')
    if (btn === null) return
    btn.addEventListener('click', () => { void open_pose_library_dialog(engine) })
  })
}

async function open_pose_library_dialog (engine: Mesh2MotionEngine): Promise<void> {
  const skeleton_type = engine.load_skeleton_step.skeleton_type()
  const poses = await PoseLibraryStorage.list(skeleton_type)
  const rows = poses.map((p) => {
    const thumb = p.thumbnail_data_url !== undefined
      ? `<img src="${p.thumbnail_data_url}" style="width:48px;height:36px;object-fit:cover;border-radius:3px;" alt="" />`
      : '<div style="width:48px;height:36px;background:#2a2a2a;border-radius:3px;"></div>'
    return `
      <li data-pose-id="${p.id}" style="display:flex;align-items:center;gap:0.5rem;padding:0.25rem 0;">
        ${thumb}
        <span style="flex:1;">${escape_html(p.name)}</span>
        <button class="secondary-button" data-pose-action="apply">Apply</button>
        <button class="no-style-button" data-pose-action="delete">🗑</button>
      </li>`
  }).join('')

  const html = `
    <div style="display:flex;flex-direction:column;gap:0.75rem;min-width:320px;">
      <div style="display:flex;gap:0.5rem;align-items:center;">
        <input type="text" id="pose-library-name-input" placeholder="Pose name" style="flex:1;" />
        <button id="pose-library-save-btn">Save current pose</button>
      </div>
      <ul id="pose-library-list" style="list-style:none;padding:0;margin:0;max-height:240px;overflow-y:auto;">
        ${poses.length === 0 ? '<li style="opacity:0.7;padding:0.5rem 0;">No saved poses yet.</li>' : rows}
      </ul>
    </div>
  `
  const dialog = new ModalDialog('Pose library', html)
  dialog.show()

  document.querySelector<HTMLButtonElement>('#pose-library-save-btn')?.addEventListener('click', () => {
    const name_input = document.querySelector<HTMLInputElement>('#pose-library-name-input')
    const name = (name_input?.value ?? '').trim()
    if (name === '') {
      new ModalDialog('Pose library', 'Give the pose a name first.').show()
      return
    }
    const skeleton = engine.weight_skin_step.skeleton()
    if (skeleton === undefined) {
      new ModalDialog('Pose library', 'No bound skeleton — finish the rig first.').show()
      return
    }
    const pose: SavedPose = {
      id: generate_pose_id(),
      name,
      skeleton_type,
      created_at: new Date().toISOString(),
      bones: capture_pose_for_library(skeleton),
      thumbnail_data_url: engine.capture_canvas_thumbnail(192, 144)
    }
    void PoseLibraryStorage.put(pose).then((saved) => {
      if (!saved) {
        new ModalDialog('Pose library', 'Could not save pose. Your browser storage may be unavailable or blocked.').show()
        return
      }
      dialog.remove()
      void open_pose_library_dialog(engine)
    })
  })

  document.querySelectorAll<HTMLLIElement>('#pose-library-list li[data-pose-id]').forEach((li) => {
    const id = li.getAttribute('data-pose-id') as string
    li.querySelector<HTMLButtonElement>('[data-pose-action="apply"]')?.addEventListener('click', () => {
      const skeleton = engine.weight_skin_step.skeleton()
      if (skeleton === undefined) {
        new ModalDialog('Pose library', 'No bound skeleton — finish the rig first.').show()
        return
      }
      const pose = poses.find(p => p.id === id)
      if (pose === undefined) return
      const applied = apply_pose_to_skeleton(skeleton, pose.bones)
      console.log(`Applied pose "${pose.name}": ${applied} bone(s) updated`)
      dialog.remove()
    })
    li.querySelector<HTMLButtonElement>('[data-pose-action="delete"]')?.addEventListener('click', () => {
      const pose = poses.find(p => p.id === id)
      if (pose === undefined) return
      void confirm_action({
        title: 'Delete pose',
        message: `Delete pose "${pose.name}"?`,
        confirm_label: 'Delete',
        danger: true
      }).then(async (confirmed) => {
        if (!confirmed) return
        const deleted = await PoseLibraryStorage.delete(id)
        if (!deleted) {
          new ModalDialog('Pose library', `Could not delete pose "${escape_html(pose.name)}". Your browser storage may be unavailable or blocked.`).show()
          return
        }
        dialog.remove()
        void open_pose_library_dialog(engine)
      })
    })
  })
}
